import React, { useState } from "react";

import { ApiError, invitesApi } from "../../api";
import { useMutation } from "../../hooks/useExclusiveMutation";
import type { InviteStatusView } from "../../types/invite";
import { cleanPhone } from "./inviteInput";

const STORAGE_PREFIX = "zoniq_invite_";

/** The guest's private key for this form, if this browser has one. Storage can be blocked. */
export function readInviteKey(formToken: string): string | null {
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + formToken);
  } catch {
    return null;
  }
}

export function writeInviteKey(formToken: string, key: string | null): void {
  try {
    if (key) window.localStorage.setItem(STORAGE_PREFIX + formToken, key);
    else window.localStorage.removeItem(STORAGE_PREFIX + formToken);
  } catch {
    // private mode / blocked storage: the WhatsApp link and "Check your status" still work
  }
}

function formatWhen(iso?: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "numeric",
    minute: "2-digit",
    day: "numeric",
    month: "short",
  });
}

function formatInr(amount?: number | null): string {
  return amount == null ? "" : `₹${amount.toLocaleString("en-IN")}`;
}

const Rule: React.FC = () => (
  <div className="inv-rule" aria-hidden="true">
    <span />
    <i />
    <span />
  </div>
);

/** A returning guest's own request: where it stands, and Pay once approved. */
export const InviteStatusPanel: React.FC<{
  formToken: string;
  accessToken: string;
  view: InviteStatusView;
  onRefresh: () => void;
  onNotMe: () => void;
}> = ({ formToken, accessToken, view, onRefresh, onNotMe }) => {
  const [error, setError] = useState("");
  const pay = useMutation({
    mutationFn: () => invitesApi.myPay(formToken, accessToken),
    onSuccess: (res) => {
      if (res.status === "payable" && res.pay_url) {
        window.location.assign(res.pay_url);
        return;
      }
      onRefresh();
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : "Something went wrong. Please try again."),
  });

  const who = view.attending === "couple" && view.partner_name ? `You + ${view.partner_name}` : "Just you";
  let title: string;
  let body: React.ReactNode;

  switch (view.status) {
    case "approved":
      title = `You're on the list, ${view.first_name}`;
      body = view.can_pay ? (
        <>
          <p>
            {who} · {view.ticket_name}
            {view.amount != null ? <> · <strong>{formatInr(view.amount)}</strong></> : null}
          </p>
          {view.hold_expires_at ? (
            <p>Your spot is held until <strong>{formatWhen(view.hold_expires_at)}</strong>.</p>
          ) : null}
          <button className="inv-submit" type="button" disabled={pay.isPending} onClick={() => pay.mutate()}>
            {pay.isPending ? "Opening payment…" : "Pay now"}
          </button>
          <p className="inv-form-error" role="alert">{error}</p>
        </>
      ) : (
        <p>Payments open shortly. We'll message you on WhatsApp the moment you can pay.</p>
      );
      break;
    case "paid":
      title = `You're in, ${view.first_name}`;
      body = (
        <>
          <p>Your pass ID is {view.application_no}. We'll send the venue and your arrival time before the event.</p>
          {view.status_url ? (
            <a className="inv-submit" href={view.status_url}>View your pass</a>
          ) : null}
        </>
      );
      break;
    case "waitlisted":
      title = "You're on the waitlist";
      body = <p>We're at capacity for now. We'll reach out on WhatsApp if a spot opens.</p>;
      break;
    case "expired":
      title = "Your hold has ended";
      body = <p>The spot went back to the waitlist. We'll reach out on WhatsApp if one opens again.</p>;
      break;
    default:
      title = "Request received";
      body = <p>Every request is reviewed personally. We'll update you here and on WhatsApp.</p>;
  }

  return (
    <section className="inv-done" aria-live="polite">
      <Rule />
      <h2>{title}</h2>
      {body}
      <span className="inv-ref">Request {view.application_no}</span>
      <button type="button" className="inv-link-btn" onClick={onNotMe}>
        Not you? Request a new invitation
      </button>
    </section>
  );
};

/** "Already requested? Check your status": WhatsApp number, then a 6-digit code. */
export const InviteLookupPanel: React.FC<{
  formToken: string;
  onVerified: (accessToken: string) => void;
  onCancel: () => void;
}> = ({ formToken, onVerified, onCancel }) => {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const send = useMutation({
    mutationFn: () => invitesApi.sendLoginCode(formToken, `+91${cleanPhone(phone)}`),
    onSuccess: () => {
      setSent(true);
      setError("");
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : "Couldn't send the code. Try again."),
  });
  const verify = useMutation({
    mutationFn: () => invitesApi.verifyLoginCode(formToken, `+91${cleanPhone(phone)}`, code.trim()),
    onSuccess: (res) => onVerified(res.access_token),
    onError: (err: unknown) =>
      setError(err instanceof ApiError || err instanceof Error ? err.message : "That code didn't work."),
  });

  const phoneOk = /^[6-9]\d{9}$/.test(cleanPhone(phone));

  return (
    <section className="inv-done" aria-live="polite">
      <Rule />
      <h2>Check your status</h2>
      {!sent ? (
        <>
          <p>Enter the WhatsApp number you applied with. We'll send you a 6-digit code.</p>
          <div className="inv-field inv-lookup-field">
            <div className="inv-phone">
              <span className="inv-cc" aria-hidden="true">+91</span>
              <input
                className="inv-control"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                placeholder="10-digit mobile"
                maxLength={14}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                aria-label="WhatsApp number"
              />
            </div>
          </div>
          <button className="inv-submit" type="button" disabled={!phoneOk || send.isPending} onClick={() => send.mutate()}>
            {send.isPending ? "Sending…" : "Send code"}
          </button>
        </>
      ) : (
        <>
          <p>If this number has requested an invitation, a code is on its way on WhatsApp.</p>
          <div className="inv-field inv-lookup-field">
            <input
              className="inv-control inv-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              aria-label="Code"
            />
          </div>
          <button className="inv-submit" type="button" disabled={code.length !== 6 || verify.isPending} onClick={() => verify.mutate()}>
            {verify.isPending ? "Checking…" : "Show my request"}
          </button>
          <button type="button" className="inv-link-btn" disabled={send.isPending} onClick={() => send.mutate()}>
            Send a new code
          </button>
        </>
      )}
      <p className="inv-form-error" role="alert">{error}</p>
      <button type="button" className="inv-link-btn" onClick={onCancel}>
        Back to the request form
      </button>
    </section>
  );
};
